import React, { useState, useEffect } from 'react';
import { api } from './api';
import PostCard from './PostCard';
import './Trash.css';

export default function Trash({ user, onPostRestore, onPostPermanentDelete }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isAdmin = user && (user.role === 'admin' || user.is_admin || user.is_superuser);

  useEffect(() => {
    // Проверяем права доступа
    if (!isAdmin) {
      setError('Доступ запрещен. Только администраторы могут просматривать корзину.');
      setLoading(false);
      return;
    }
    loadTrashPosts();
  }, [isAdmin]);

  const loadTrashPosts = async () => {
    try {
      setLoading(true);
      const data = await api.getTrashPosts();
      setPosts(data);
      setError(null);
    } catch (err) {
      setError(err.message || 'Ошибка при загрузке корзины');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (postId) => {
    try {
      await api.restorePost(postId);
      setPosts(posts.filter(p => p.id !== postId));
      if (onPostRestore) {
        onPostRestore(postId);
      }
    } catch (err) {
      alert(err.message || 'Ошибка при восстановлении поста');
    }
  };

  const handlePermanentDelete = async (postId) => {
    if (!window.confirm('Вы уверены, что хотите полностью удалить этот пост? Это действие нельзя отменить.')) {
      return;
    }
    
    try {
      await api.permanentDeletePost(postId);
      setPosts(posts.filter(p => p.id !== postId));
      if (onPostPermanentDelete) {
        onPostPermanentDelete(postId);
      }
    } catch (err) {
      alert(err.message || 'Ошибка при удалении поста');
    }
  };

  if (loading) {
    return (
      <div className="trash-container">
        <div className="trash-loading">Загрузка корзины...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="trash-container">
        <div className="trash-error">Доступ запрещен. Только администраторы могут просматривать корзину.</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="trash-container">
        <div className="trash-error">Ошибка: {error}</div>
      </div>
    );
  }

  return (
    <div className="trash-container">
      <div className="trash-header">
        <h2 className="trash-title">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
          Корзина
        </h2>
        <p className="trash-subtitle">
          Удаленные посты. Вы можете восстановить их или удалить навсегда.
        </p>
      </div>

      {posts.length === 0 ? (
        <div className="trash-empty">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
          <p>Корзина пуста</p>
        </div>
      ) : (
        <div className="trash-posts">
          {posts.map((post, index) => (
            <div key={post.id} className="trash-post-wrapper">
              <PostCard 
                post={post} 
                index={index}
                user={user}
                onPostUpdate={() => {}} // Не обновляем, так как это корзина
              />
              <div className="trash-deletion-info">
                <div className="trash-deletion-info-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  <span className="trash-deletion-label">Удален:</span>
                  <span className="trash-deletion-value">
                    {post.deleted_by_name || post.deleted_by_username || 'Неизвестно'}
                  </span>
                </div>
                {post.deleted_at && (
                  <div className="trash-deletion-info-item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    <span className="trash-deletion-label">Дата удаления:</span>
                    <span className="trash-deletion-value">
                      {new Date(post.deleted_at).toLocaleString('ru-RU', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                )}
              </div>
              <div className="trash-post-actions">
                <button
                  className="trash-restore-button"
                  onClick={() => handleRestore(post.id)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
                    <path d="M21 3v5h-5"></path>
                    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path>
                    <path d="M3 21v-5h5"></path>
                  </svg>
                  Восстановить
                </button>
                <button
                  className="trash-delete-button"
                  onClick={() => handlePermanentDelete(post.id)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                  Удалить навсегда
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

